import { Component } from '@theme/component';

/**
 * <tmc-gallery>: a sideways card gallery (Apple "scroll gallery"). Native horizontal scrolling with snap
 * does the work on every device; on desktop two round arrow buttons page through it and grey out at the
 * ends. Without JS the arrows stay hidden and the row still scrolls.
 *
 * @typedef {{ track: HTMLElement, prev?: HTMLButtonElement, next?: HTMLButtonElement }} Refs
 * @extends {Component<Refs>}
 */
class TmcGallery extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #abort = null;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { track, prev, next } = this.refs;
    const signal = this.#abort.signal;
    this.dataset.enhanced = '';
    prev?.addEventListener('click', () => this.#page(-1), { signal });
    next?.addEventListener('click', () => this.#page(1), { signal });
    track.addEventListener('scroll', () => this.#queue(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queue(), { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /** Scrolls by roughly one screenful of cards, landing on a card edge (snap does the rest). @param {1 | -1} direction */
  #page(direction) {
    const { track } = this.refs;
    const card = /** @type {HTMLElement | null} */ (track.firstElementChild);
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card ? card.offsetWidth + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    track.scrollBy({ left: direction * step * perPage, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  #queue() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  #update() {
    const { track, prev, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
    this.toggleAttribute('data-scrollable', max > 2);
  }
}

if (!customElements.get('tmc-gallery')) customElements.define('tmc-gallery', TmcGallery);
